import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Spinner } from '@crisp/crisp-ui/cabbage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faChevronRight } from '@crisp/crisp-ui/icons/classic/regular';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { TableOfContents } from './TableOfContents';
import { FeedbackWidget } from './FeedbackWidget';
import { PrevNextNav } from './PrevNextNav';
import { CopyPageButton } from './CopyPageButton';
import { AskBar } from './AskBar';
import { SearchModal } from '../search/SearchModal';
import { HastRenderer } from '../../lib/renderer';
import { AuthRequiredCard, useCrispAuth, useUserAccess } from '../../lib/auth0';
import { trackPageView } from '../../lib/analytics';
import { filterNavByAccess } from '../../lib/navAccess';
import { NavOutput, PageOutput, NavPageItem, NavGroup } from '../../types';
import type { SiteConfig } from '../../lib/sites';
import { cn } from '../../lib/cn';
import styles from './Layout.module.css';

function formatSlugTitle(slug: string): string {
  if (!slug || slug === 'index') return 'Welcome to Crisp Support';
  const last = slug.split('/').pop() || slug;
  return last
    .replace(/^articles-/, '')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

interface LayoutProps {
  navData: NavOutput | null;
  pageData: PageOutput | null;
  isLoading?: boolean;
  error?: string | null;
  routePrefix?: string;
  site?: SiteConfig;
  previewBranch?: string;
}

export const Layout: React.FC<LayoutProps> = ({
  navData,
  pageData,
  isLoading = false,
  error = null,
  routePrefix = '',
  site,
  previewBranch,
}) => {
  const location = useLocation();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchMode, setSearchMode] = useState<'keyword' | 'ai'>('keyword');
  const [searchInitialQuery, setSearchInitialQuery] = useState<string>('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    if (!site) return;
    document.documentElement.dataset.site = site.id;
    document.title = pageData?.title
      ? `${pageData.title} | ${site.name}`
      : `${site.name} Documentation`;
  }, [site, pageData?.title]);

  // Check access permissions for current page
  const pageAccess = pageData?.access || ['public'];
  const { hasAccess } = useUserAccess(pageAccess);

  const { isAuthenticated, roles, isAdmin } = useCrispAuth();
  // Signed in without the claims this site's login needs: the site treats them as a visitor.
  const loginDenied = Boolean(navData?.login_denied);
  const rolesKey = (roles || []).join('|');
  const groups = useMemo(
    () =>
      filterNavByAccess(navData?.navigation?.groups || [], {
        isAuthenticated: isAuthenticated && !loginDenied,
        roles,
        isAdmin,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navData, isAuthenticated, loginDenied, rolesKey, isAdmin]
  );

  // Scroll to top or anchor on route changes
  useEffect(() => {
    setIsSidebarOpen(false);

    if (location.hash) {
      const id = location.hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  // A link into another page arrives before that page's content does, so the
  // anchor only exists once it has rendered.
  useEffect(() => {
    if (!pageData || !location.hash) return;
    const el = document.getElementById(location.hash.slice(1));
    if (el) {
      const timer = window.setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
      return () => window.clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageData?.slug]);

  // Track page view in GA4 & Amplitude
  useEffect(() => {
    if (pageData) {
      trackPageView(pageData.slug, pageData.title);
    }
  }, [pageData?.slug, pageData?.title]);

  const [canResumeAnswer, setCanResumeAnswer] = useState(false);
  // Bumped with each brand-new question, so the panel sees the reset and the
  // question in the same render and handles them in that order.
  const [conversationKey, setConversationKey] = useState(0);

  const openSearch = () => {
    setCanResumeAnswer(false);
    setSearchMode('keyword');
    setSearchInitialQuery('');
    setIsSearchOpen(true);
  };

  const openAssistant = (initialQuery?: string) => {
    setCanResumeAnswer(false);
    if (initialQuery?.trim()) setConversationKey((key) => key + 1);
    setSearchMode('ai');
    setSearchInitialQuery(initialQuery || '');
    setIsSearchOpen(true);
  };

  // Keyboard shortcut Cmd+K / Ctrl+K and Cmd+I / Ctrl+I
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        openSearch();
      } else if ((e.metaKey || e.ctrlKey) && e.key === 'i') {
        e.preventDefault();
        openAssistant();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Compute group title (eyebrow)
  const groupTitle = useMemo(() => {
    if (!pageData) return 'Home';
    const slug = pageData.slug;
    if (slug === '' || slug === 'index') return 'Home';

    const findGroup = (
      items: Array<NavGroup | NavPageItem>,
      currentGroupName: string
    ): string | null => {
      for (const item of items) {
        if ('group' in item && item.pages) {
          const match = findGroup(item.pages, item.group);
          if (match) return match;
        } else if ('slug' in item && item.slug === slug) {
          return currentGroupName;
        }
      }
      return null;
    };

    const found = findGroup(groups, 'Home');
    return found || 'Home';
  }, [groups, pageData]);

  // Compute Previous and Next pages from navigation groups
  const { prevPage, nextPage } = useMemo(() => {
    if (!pageData) return { prevPage: null, nextPage: null };

    const flatPages: Array<{ slug: string; title: string }> = [];

    function flatten(items: Array<NavPageItem | NavGroup>) {
      for (const item of items) {
        if ('pages' in item) {
          flatten(item.pages);
        } else {
          flatPages.push({
            slug: item.slug,
            title: item.sidebarTitle || item.title || formatSlugTitle(item.slug),
          });
        }
      }
    }

    flatten(groups);

    const currentIndex = flatPages.findIndex(
      (p) =>
        p.slug === pageData.slug ||
        (p.slug === 'index' && (pageData.slug === '' || pageData.slug === 'index'))
    );

    return {
      prevPage: currentIndex > 0 ? flatPages[currentIndex - 1] : null,
      nextPage:
        currentIndex >= 0 && currentIndex < flatPages.length - 1
          ? flatPages[currentIndex + 1]
          : null,
    };
  }, [groups, pageData]);

  // Check if wide mode
  const isWideMode =
    pageData?.mode === 'wide' ||
    pageData?.frontmatter?.mode === 'wide' ||
    pageData?.slug === 'index' ||
    pageData?.slug === '';

  return (
    <div className={styles.page}>
      <Header
        routePrefix={routePrefix}
        site={site}
        previewBranch={previewBranch}
        onOpenSearch={openSearch}
        onOpenAssistant={openAssistant}
        onToggleSidebar={() => setIsSidebarOpen((prev) => !prev)}
        isSidebarOpen={isSidebarOpen}
      />

      {previewBranch && (
        <div className={styles.previewBanner}>
          Rendering GitHub branch <strong>{previewBranch}</strong> in Crisp Docs.{' '}
          <a href="/admin/author">Back to authoring</a>
        </div>
      )}

      <div className={styles.mobileCrumb}>
        <button
          type="button"
          onClick={() => setIsSidebarOpen(true)}
          className={styles.crumbButton}
        >
          <FontAwesomeIcon icon={faBars} className="icon-slot" />
          <span className={styles.crumbGroup}>{groupTitle}</span>
          <FontAwesomeIcon icon={faChevronRight} className={`icon-slot ${styles.crumbSep}`} />
          <span className={styles.crumbCurrent}>
            {pageData?.title || 'Welcome to Crisp Support'}
          </span>
        </button>
      </div>

      <div className={styles.shell}>
        <Sidebar
          groups={groups}
          routePrefix={routePrefix}
          homeSlug={site?.homeSlug}
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />

        <main className={styles.main}>
          {isLoading ? (
            <div className={styles.loading}>
              <Spinner />
              <p className={styles.loadingLabel}>Loading documentation...</p>
            </div>
          ) : error ? (
            <div className={styles.error}>
              <h2 className={styles.errorTitle}>Page Not Found</h2>
              <p className={styles.errorBody}>
                The requested document could not be found or has moved.
              </p>
              <Button render={<a href={routePrefix || '/'} />}>
                Return to Documentation Home
              </Button>
            </div>
          ) : pageData ? (
            <article
              key={location.pathname}
              className={cn(styles.article, isWideMode && styles.articleWide)}
            >
              <header id="header" className={styles.articleHeader}>
                <div>
                  {groupTitle && <div className={styles.eyebrow}>{groupTitle}</div>}
                  <div className={styles.titleRow}>
                    <h1 id="page-title" className={styles.pageTitle}>
                      {pageData.title}
                    </h1>
                    {!previewBranch && <CopyPageButton slug={pageData.slug} site={site} />}
                  </div>
                  {pageData.description && (
                    <p className={styles.description}>{pageData.description}</p>
                  )}
                </div>
              </header>

              {!hasAccess ? (
                <AuthRequiredCard access={pageAccess} title={pageData.title} loginDenied={loginDenied} />
              ) : (
                <div className="mdx-content">
                  <HastRenderer
                    key={location.pathname}
                    hast={pageData.hast}
                    routePrefix={routePrefix}
                  />
                </div>
              )}

              <PrevNextNav prev={prevPage} next={nextPage} routePrefix={routePrefix} />

              {!previewBranch && <FeedbackWidget slug={pageData.slug} />}
            </article>
          ) : null}
        </main>

        {pageData && hasAccess && !isWideMode && (
          <TableOfContents headings={pageData.headings || []} pageSlug={pageData.slug} />
        )}
      </div>

      {!previewBranch && (
        <AskBar
          onOpenAssistant={openAssistant}
          onResumeAnswer={canResumeAnswer && !isSearchOpen ? () => openAssistant() : undefined}
        />
      )}

      {!previewBranch && (
        <SearchModal
          isOpen={isSearchOpen}
          initialMode={searchMode}
          initialQuery={searchInitialQuery}
          conversationKey={conversationKey}
          currentPageSlug={pageData?.slug}
          currentPageTitle={pageData?.title}
          site={site}
          onClose={() => setIsSearchOpen(false)}
          onLeaveConversation={() => setCanResumeAnswer(true)}
        />
      )}
    </div>
  );
};

export default Layout;
